/**
 * ProductDetailPage — PDP (fully wired)
 *
 * Config-driven product detail page.
 * Loads product by itemCode, resolves PDP sections + field config,
 * and renders real components in accordion sections.
 */
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { getProductByItemCode, loadAllProducts } from '@services/dataService';
import { getPDPSections, getPDPFieldConfig, PDP_CONFIG, isCommerceEnabledForProductLine, getProductDisplayName } from '@data/index';
import { loadContentOverlay, getContentForProduct } from '@data/contentOverlay';
import { findSimilarProducts, getPremiumAlternative, findCompatibleProducts } from '@services/recommendationService';
import { ExternalLink } from 'lucide-react';
import Breadcrumbs from '@components/layout/Breadcrumbs';
import ProductDetailHero from '@components/product/ProductDetailHero';
import TechDocLink from '@components/product/TechDocLink';
import PDPAccordionSection from '@components/product/PDPAccordionSection';
import SpecTable from '@components/product/SpecTable';
import OverviewSection from '@components/product/OverviewSection';
import CompetitorXrefTable from '@components/product/CompetitorXrefTable';
import RelatedProductsCarousel from '@components/product/RelatedProductsCarousel';
import SimilarProductsSection from '@components/product/SimilarProductsSection';
import OutphasedRecommendationModal from '@components/product/OutphasedRecommendationModal';
import StickyCompareBar from '@components/compare/StickyCompareBar';
import { useAuth } from '@contexts/AuthContext';
import { useCart } from '@contexts/CartContext';
import { useContactModal } from '@contexts/ContactModalContext';
import Toast from '@components/common/Toast';
import AddToFavouritesModal from '@components/favourites/AddToFavouritesModal';

/**
 * Build the images array for ImageCarousel from product data.
 * Uses imageUrl / imageUrl2 fields enriched from image_urls.xlsx (Azure Blob SAS URLs).
 * Falls back to empty array (ImageCarousel renders its own "No image" placeholder).
 */
function buildProductImages(product) {
  // If the product already has a manually-set images array, use it
  if (product.images && product.images.length > 0) return product.images;

  const images = [];

  if (product.imageUrl) {
    images.push({
      src: product.imageUrl,
      alt: `${product.description} - Main`,
      thumbnailSrc: product.imageUrl,
    });
  }

  if (product.imageUrl2) {
    images.push({
      src: product.imageUrl2,
      alt: `${product.description} - Alternate`,
      thumbnailSrc: product.imageUrl2,
    });
  }

  return images;
}

/**
 * Format a subcategory slug into a display label.
 * e.g. "shank-adapters" -> "Shank adapters"
 */
function formatSubcategory(slug) {
  if (!slug) return '';
  return slug
    .replace(/-/g, ' ')
    .replace(/^\w/, c => c.toUpperCase());
}

export default function ProductDetailPage() {
  const { itemCode, productId } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [sections, setSections] = useState([]);
  const [fieldConfig, setFieldConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [replacementProduct, setReplacementProduct] = useState(null);
  const [showOutphasedModal, setShowOutphasedModal] = useState(false);
  const [favProduct, setFavProduct] = useState(null);
  const [overlayContent, setOverlayContent] = useState(null);

  // Resolve itemCode from either the new or legacy route param
  const resolvedItemCode = itemCode || productId;

  // Auth integration
  const { userType, isAuthenticated } = useAuth();
  const userState = useMemo(() => {
    if (!isAuthenticated) return 'guest';
    if (userType === 'customer') return 'customer';
    if (userType === 'employee') return 'employee-viewer';
    return 'guest';
  }, [isAuthenticated, userType]);

  // Cart integration
  const { dispatch } = useCart();
  const { openContact } = useContactModal();
  const [qty, setQty] = useState(1);
  const [toast, setToast] = useState(null);

  function handleAddToCart() {
    if (!product) return;
    dispatch({
      type: 'ADD_ITEM',
      payload: {
        itemCode: product.itemCode,
        description: product.description,
        qty,
        unitPrice: product.price || 0,
        currency: product.currency || 'CAD',
        image: product.images?.[0]?.src || product.imageUrl || '/images/placeholders/test-product-1.png',
      },
    });
    setToast({ message: `${product.description} added to cart`, type: 'success' });
    setTimeout(() => setToast(null), 3000);
  }

  // Section renderer map
  const SECTION_COMPONENTS = {
    overview: OverviewSection,
    specifications: SpecTable,
    competitorXref: CompetitorXrefTable,
    relatedProducts: RelatedProductsCarousel,
    similarProducts: SimilarProductsSection,
  };

  useEffect(() => {
    if (!resolvedItemCode) {
      setError('No item code provided');
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchProduct() {
      setLoading(true);
      setError(null);

      try {
        const [prod, products] = await Promise.all([
          getProductByItemCode(resolvedItemCode),
          loadAllProducts(),
        ]);

        if (cancelled) return;

        if (!prod) {
          setError('Product not found');
          setLoading(false);
          return;
        }

        setProduct(prod);
        setAllProducts(products || []);

        // D5 — hydrate PDP with content-overlay entry (crawl-sourced
        // description / features / downloads / specsTable / sourceUrl).
        // Falls through cleanly when overlay has no entry for this itemCode.
        loadContentOverlay().then(() => {
          if (!cancelled) setOverlayContent(getContentForProduct(prod.itemCode));
        });

        // Derive PDP config from product metadata
        const productLine = prod.productLine;
        const categorySlug = prod.subcategory;

        const pdpSections = getPDPSections(categorySlug, productLine);
        const pdpFields = getPDPFieldConfig(categorySlug, productLine);

        setSections(pdpSections || []);
        setFieldConfig(pdpFields || []);

        // Check if product is outphased and find replacement
        const isOutphased = prod.outphasedReason || prod.supersessionReason || prod.recommendedItemCode || prod.supersededBy;
        if (isOutphased) {
          const replacementCode = prod.recommendedItemCode || prod.supersededBy;
          if (replacementCode && products) {
            const replacement = products.find(p => p.itemCode === replacementCode);
            setReplacementProduct(replacement || null);
          }
          // Only show modal once per product per session
          const shownKey = `outphased-shown-${resolvedItemCode}`;
          try {
            if (!sessionStorage.getItem(shownKey)) {
              setShowOutphasedModal(true);
            }
          } catch (e) {
            setShowOutphasedModal(true);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[ProductDetailPage] Load failed:', err);
          setError('Failed to load product');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchProduct();
    return () => { cancelled = true; };
  }, [resolvedItemCode]);

  // Loading state
  if (loading) {
    return (
      <div className="sandvik-container py-6 flex flex-col items-center justify-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-n3 border-t-electric-blue mb-4" />
        <p className="text-n5 font-sandvik-text">Loading product...</p>
      </div>
    );
  }

  // Error / Not Found state
  if (error || !product) {
    return (
      <div className="sandvik-container py-6 flex flex-col items-center justify-center min-h-[40vh]">
        <span className="material-symbols-outlined text-4xl text-n4 mb-3">error_outline</span>
        <h1 className="font-sandvik-headline font-normal text-xl text-carbon-black mb-2">
          Product not found
        </h1>
        <p className="text-n5 font-sandvik-text mb-4">
          {error || 'The requested product could not be found.'}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="text-electric-blue hover:underline font-sandvik-text text-sm"
        >
          &larr; Go back
        </button>
      </div>
    );
  }

  // Breadcrumb items — product-line resolution.
  //
  // RAISEBORING WAS MISSING AND THE FALLBACK CLAIMED TOP HAMMER. This map was
  // written as a three-way TH / DTH / rotary resolution, so a raiseboring
  // product matched nothing and `|| PRODUCT_LINE_META['top-hammer']` labelled
  // it "Top Hammer" — e.g. 7008-6141-77, a raiseboring cutter, read
  // "home > Top Hammer > Raiseboring cutters". The source data was never wrong:
  // every record in products-raiseboring.json carries productLine
  // "raiseboring". Fixed 2026-08-17.
  //
  // Route and label follow navigationContent.json's productLines.raiseboring
  // (route "/products/raiseboring", shortTitle "Raiseboring") rather than being
  // invented here, so the breadcrumb and the mega menu cannot drift apart.
  const PRODUCT_LINE_META = {
    'top-hammer': { label: 'Top Hammer', path: '/products/top-hammer' },
    'down-the-hole': { label: 'Down the Hole', path: '/products/down-the-hole' },
    'dth': { label: 'Down the Hole', path: '/products/down-the-hole' },
    'rotary': { label: 'Rotary', path: '/products/rotary' },
    'raiseboring': { label: 'Raiseboring', path: '/products/raiseboring' },
    // 111 resharpening products carry productLine "services" and were reading
    // "Top Hammer" for the same reason. Label and route from
    // navigationContent.json's productLines.services.
    'services': { label: 'Services', path: '/products/services' },
    // NOT MAPPED, DELIBERATELY: productLine "rt" — 179 items, every one of them
    // subcategory "drill-pipes", named DP076/… and sitting in
    // products-dth.json. They look like ROTARY drill pipes, but "looks like" is
    // not good enough to put a product line in a breadcrumb, and which line
    // they belong to is a data question for the catalogue owner rather than a
    // guess for this file. Until it is answered they render with no product-line
    // crumb, which says nothing rather than something wrong. Raised 2026-08-17.
  };
  // NO DEFAULT PRODUCT LINE. An unrecognised value now OMITS the segment rather
  // than asserting a wrong one: a missing breadcrumb crumb is a cosmetic gap,
  // whereas naming someone else's product line is a statement the page cannot
  // support. If a new line appears here unlabelled, add it above.
  const plMeta = PRODUCT_LINE_META[product.productLine] || null;
  const productLinePath = plMeta ? plMeta.path : '/products';

  const breadcrumbItems = [
    ...(plMeta ? [{ label: plMeta.label, to: plMeta.path }] : []),
    { label: formatSubcategory(product.subcategory), to: `${productLinePath}/${product.subcategory}` },
    { label: getProductDisplayName(product), to: null, isLast: true },
  ];

  // Cross-category related-product resolver — dispatches by subcategory.
  // Returns the products list + a section title override so the accordion
  // reads "Compatible grinding cups" on a machine PDP, "Compatible grinding
  // machines" on a cup PDP, and the existing "You may be also interested in"
  // for DTH hammer → spares.
  function resolveRelatedProducts() {
    if (!product) return { products: [], title: null };

    // Data-driven cross-category (machines ↔ cups)
    const compat = findCompatibleProducts(product, allProducts);
    if (compat.length > 0) {
      if (product.subcategory === 'services-resharpening-machines') {
        return { products: compat, title: 'Compatible grinding cups' };
      }
      if (product.subcategory === 'services-resharpening-cups') {
        return { products: compat, title: 'Compatible grinding machines' };
      }
      if (product.subcategory === 'shank-adapters') {
        return { products: compat, title: 'Other shanks that fit this drill' };
      }
    }

    // Legacy DTH hammer → spares (existing slug convention)
    if (product.subcategory?.endsWith('-hammers')) {
      const products = allProducts.filter((p) =>
        p.subcategory === product.subcategory?.replace('-hammers', '-spares') &&
        p.productLine === product.productLine
      ).slice(0, 8);
      return { products, title: null };
    }

    return { products: [], title: null };
  }

  // Section props getter
  function getSectionProps(sectionKey) {
    const base = { product };

    switch (sectionKey) {
      case 'specifications':
        return {
          ...base,
          fieldConfig,
          specGroups: (product.productLine === 'dth' || product.productLine === 'down-the-hole') &&
            product.subcategory?.includes('hammer') ? PDP_CONFIG.dth?.hammerSpecGroups : undefined,
        };
      case 'competitorXref':
        return { ...base, manufacturers: PDP_CONFIG.competitorManufacturers };
      case 'relatedProducts': {
        const { products } = resolveRelatedProducts();
        return { products, userState, onViewDetails: (code) => navigate(`/products/detail/${code}`) };
      }
      case 'similarProducts':
        return { ...base, allProducts, userState, onNavigate: (code) => navigate(`/products/detail/${code}`) };
      case 'overview':
      default:
        return base;
    }
  }

  // Pre-compute which sections would render empty so the accordion header
  // is hidden entirely (not just collapsed with no content). Vandana asked
  // for HIDE not remove — accordion stays wired via categorySections so
  // when data flows in the section reappears without a config change.
  function sectionWouldBeEmpty(sectionKey) {
    if (!product) return true;
    if (sectionKey === 'similarProducts') {
      const similar = findSimilarProducts(product, allProducts || []);
      const premium = getPremiumAlternative(product, allProducts || []);
      return similar.length === 0 && !premium;
    }
    if (sectionKey === 'relatedProducts') {
      return resolveRelatedProducts().products.length === 0;
    }
    return false;   // overview / specifications / competitorXref always render
  }

  // Loaded state
  return (
    <div className="sandvik-container py-6">
      <Breadcrumbs items={breadcrumbItems} />

      {/* Hero — inject dev images until real image URLs are provided.
          The "Add to wishlist" button lives inside the hero and calls
          setFavProduct to open the AddToFavouritesModal. */}
      <ProductDetailHero
        product={{ ...product, images: buildProductImages(product) }}
        fieldConfig={fieldConfig}
        userState={userState}
        availability={null}
        onAddToFavourites={setFavProduct}
        overlayContent={overlayContent}
      />

      {/* Add to Cart bar — authenticated users on commerce-enabled product lines only.
          Favourites are handled by the hero's "Add to wishlist" button, so this
          bar is Qty + Add to cart only. Non-commerce lines skip the bar entirely. */}
      {isAuthenticated && isCommerceEnabledForProductLine(product.productLine) && (
        <div className="flex items-center gap-4 mt-4 mb-6 p-4 bg-n1 border border-n2">
          <label className="text-sm font-sandvik-text text-n5" htmlFor="pdp-qty">Qty</label>
          <input
            id="pdp-qty"
            type="number"
            min="1"
            max="9999"
            value={qty}
            onChange={e => setQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
            className="w-20 px-2 py-1.5 border border-n3 text-sm font-sandvik-text
                       text-carbon-black focus:outline-none focus:border-electric-blue"
          />
          <button
            type="button"
            onClick={handleAddToCart}
            className="flex items-center gap-2 px-5 py-2 bg-electric-blue text-white
                       text-sm font-sandvik-text rounded-none hover:bg-eb-3 transition-colors"
          >
            <span className="material-symbols-outlined text-lg">add_shopping_cart</span>
            Add to cart
          </button>
        </div>
      )}

      {/* Technical documentation link */}
      <TechDocLink
        pdfUrl={product.techDocUrl || product.pdfUrl}
        productName={product.description}
      />

      {/* Contextual contact actions — per live MRT §7e */}
      <div className="flex flex-wrap gap-3 mb-6">
        <button
          type="button"
          onClick={() => openContact('product-info', {
            productName: product.description,
            sku: product.itemCode,
          })}
          className="flex items-center gap-2 px-4 py-2 border border-n2 text-sm font-sandvik-text
                     text-carbon-black hover:border-electric-blue hover:text-electric-blue transition-colors"
        >
          <span className="material-symbols-outlined text-lg">info</span>
          Product enquiry
        </button>
        {isAuthenticated && isCommerceEnabledForProductLine(product.productLine) && (
          <>
            <button
              type="button"
              onClick={() => openContact('request-availability', {
                productName: product.description,
                sku: product.itemCode,
                quantity: String(qty),
              })}
              className="flex items-center gap-2 px-4 py-2 border border-n2 text-sm font-sandvik-text
                         text-carbon-black hover:border-electric-blue hover:text-electric-blue transition-colors"
            >
              <span className="material-symbols-outlined text-lg">inventory</span>
              Check availability
            </button>
            <button
              type="button"
              onClick={() => openContact('request-alternate', {
                productName: product.description,
                sku: product.itemCode,
              })}
              className="flex items-center gap-2 px-4 py-2 border border-n2 text-sm font-sandvik-text
                         text-carbon-black hover:border-electric-blue hover:text-electric-blue transition-colors"
            >
              <span className="material-symbols-outlined text-lg">swap_horiz</span>
              Request alternate
            </button>
          </>
        )}
      </div>

      {/* D5 — Overlay-sourced features (marketing bullets from Sandvik.com).
          Renders above the accordion; skipped when overlay has no entry. */}
      {overlayContent?.features?.length > 0 && (
        <div className="mb-8">
          <h2 className="font-sandvik-headline font-normal text-xl text-carbon-black mb-4">
            Features & Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {overlayContent.features.map((f, i) => (
              <div key={i} className="border-l-4 border-sandvik-gold bg-n1/40 p-5">
                <h3 className="font-sandvik-text font-medium text-carbon-black mb-2">
                  {f.title}
                </h3>
                {f.body && (
                  <p className="text-n7 font-sandvik-text text-sm leading-relaxed">
                    {f.body}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* D5 — Downloads panel (spec sheets, handbooks, safety posters from crawl). */}
      {overlayContent?.downloads?.length > 0 && (
        <div className="mb-8">
          <h2 className="font-sandvik-headline font-normal text-xl text-carbon-black mb-4">
            Downloads
          </h2>
          <ul className="space-y-2">
            {overlayContent.downloads.map((dl, i) => (
              <li key={i}>
                <a
                  href={dl.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-electric-blue font-sandvik-text text-sm hover:underline"
                >
                  <ExternalLink size={14} />
                  {dl.title}
                  {dl.sizeLabel && <span className="text-n5">({dl.sizeLabel})</span>}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Accordion sections from config. Empty sections (Similar / Related
          with no matches) are HIDDEN — the categorySections entry stays so
          the section reappears the moment there's data to show. */}
      <div className="mb-6">
        {sections.map((sectionKey) => {
          const SectionComponent = SECTION_COMPONENTS[sectionKey];
          if (!SectionComponent) return null;
          if (sectionWouldBeEmpty(sectionKey)) return null;
          const sectionConf = PDP_CONFIG.sectionConfig?.[sectionKey] || {};
          const sectionProps = getSectionProps(sectionKey);
          // relatedProducts carries a dynamic title (e.g. "Compatible
          // grinding cups") when the resolver picks a cross-category match.
          let title = sectionConf.title || sectionKey;
          if (sectionKey === 'relatedProducts') {
            const dyn = resolveRelatedProducts().title;
            if (dyn) title = dyn;
          }
          return (
            <PDPAccordionSection
              key={sectionKey}
              title={title}
              defaultExpanded={sectionConf.defaultExpanded || false}
            >
              <SectionComponent {...sectionProps} />
            </PDPAccordionSection>
          );
        })}
      </div>

      {/* Outphased modal */}
      <OutphasedRecommendationModal
        isOpen={showOutphasedModal}
        onClose={() => {
          setShowOutphasedModal(false);
          try { sessionStorage.setItem(`outphased-shown-${resolvedItemCode}`, 'true'); } catch(e) {}
        }}
        product={product}
        replacementProduct={replacementProduct}
        onReplacementClick={(code) => {
          setShowOutphasedModal(false);
          navigate(`/products/detail/${code}`);
        }}
      />

      {/* Add to Favourites modal */}
      <AddToFavouritesModal
        product={favProduct}
        isOpen={!!favProduct}
        onClose={() => setFavProduct(null)}
        onSuccess={(msg) => {
          setToast({ type: 'success', message: msg });
          setFavProduct(null);
        }}
      />

      {/* Sticky compare bar (renders from context, always in DOM) */}
      <StickyCompareBar />

      {/* Toast notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type || 'success'}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
